import Link from "@mui/material/Link";
import {AggregationSurveySummarySurvey} from "crqlar-react-common/dist/models/aggregation/surveyAggregation.model";
import {useTranslation} from "react-i18next";
import {Link as RouterLink} from "react-router-dom";
import {GenericInsightsCard} from "../GenericInsightsCard";
import {useInsightsFormatters} from "../../../insights/kit/insightsFormat";
import {InsightsLoading} from "../../../insights/kit/insightsLoading";
import {InsightsColumn, InsightsTable} from "../../../insights/kit/InsightsTable";
import {SURVEY_TYPE_LABELS} from "../SurveyFactsCard";

const TABLE_SKELETON_HEIGHT = 300;

interface Props {
    surveys?: AggregationSurveySummarySurvey[];
    loading?: InsightsLoading;
}

export function BySurveyCard({surveys, loading}: Props) {
    const {t} = useTranslation();

    const {count: formatCount, percent: formatPercent} = useInsightsFormatters();

    const rows = surveys ?? [];
    const hasCompare = rows.some(survey => survey.compareSubmissions != null);

    const columns: Array<InsightsColumn<AggregationSurveySummarySurvey> | false> = [
        {
            key: "survey",
            kind: "label",
            label: t("Survey"),
            // Inline so the cell's ellipsis still clips it; the cell only sets the full-name tooltip for plain strings.
            cell: survey => (
                <Link
                    component={RouterLink}
                    to={`/system/surveys/${survey.id}`}
                    underline={"hover"}
                    color={"inherit"}
                    title={survey.name ?? survey.id}
                >
                    {survey.name ?? survey.id}
                </Link>
            ),
        },
        {key: "type", kind: "text", label: t("Type"), cell: survey => (survey.type == null ? "—" : t(SURVEY_TYPE_LABELS[survey.type]))},
        {key: "invitations", kind: "detail", label: t("Invited"), cell: survey => survey.invitations, format: formatCount},
        {key: "started", kind: "detail", label: t("Started"), cell: survey => survey.started, format: formatCount},
        {key: "submissions", kind: "value", label: t("Submitted"), cell: survey => survey.submissions, format: formatCount},
        hasCompare && {key: "compare", kind: "compare", label: t("Compare period"), cell: survey => survey.compareSubmissions, format: formatCount},
        hasCompare && {
            key: "change",
            kind: "delta",
            label: t("Change"),
            cell: survey => (survey.compareSubmissions == null ? null : survey.submissions - survey.compareSubmissions),
            format: formatCount,
            comparison: true,
        },

        {key: "responseRate", kind: "value", label: t("Response rate"), cell: survey => survey.responseRate, format: formatPercent},
    ];

    return (
        <GenericInsightsCard
            title={t("By survey")}
            hint={t("Every survey of the organisation with its invitations, started fill-outs and submissions in the period, and the response rate those make. Targets are judged on each survey's Insights page, not here.")}
            loading={loading}
            skeletonHeight={TABLE_SKELETON_HEIGHT}
        >
            <InsightsTable label={t("Survey")} columns={columns} rows={rows} rowKey={survey => survey.id}/>
        </GenericInsightsCard>
    );
}
